import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  getBillingSummary,
  recordDoctorPayment,
  getDoctorPayments,
  setDoctorRate,
  setBillingRate,
} from '../services/api/apis';
import type { BillingDoctorEntry, BillingSummary, DoctorPaymentRecord } from '../services/api/apis';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  pending:   'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  assigned:  'bg-blue-100   text-blue-700   dark:bg-blue-900/30   dark:text-blue-300',
  submitted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  completed: 'bg-green-100  text-green-700  dark:bg-green-900/30  dark:text-green-300',
  issued:    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  cancelled: 'bg-red-100    text-red-700    dark:bg-red-900/30    dark:text-red-300',
};

function fmtINR(n: number) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(n);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

// ─── RecordPaymentModal ───────────────────────────────────────────────────────

interface RecordPaymentModalProps {
  entry: BillingDoctorEntry;
  onConfirm: (amount: number, note: string) => void;
  onClose: () => void;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({ entry, onConfirm, onClose }) => {
  const [amount, setAmount] = useState(String(entry.current_balance));
  const [note, setNote]     = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">Record Payment</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl px-4 py-3 text-sm">
          <p className="font-semibold text-blue-900 dark:text-blue-100">Dr. {entry.name}</p>
          <p className="text-blue-700 dark:text-blue-300 mt-0.5">
            {entry.unpaid_case_count} billable case{entry.unpaid_case_count !== 1 ? 's' : ''} × {fmtINR(entry.rate)} = {fmtINR(entry.current_balance)}
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Amount (₹)
            </label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Note (optional)
            </label>
            <input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. UPI transfer, bank transfer ref #1234"
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(Number(amount) || 0, note)}
            disabled={!amount || Number(amount) <= 0}
            className="flex-1 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors">
            Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── DoctorPaymentCard ────────────────────────────────────────────────────────

interface DoctorPaymentCardProps {
  entry: BillingDoctorEntry;
  onSetRate: (doctorId: string, currentRate: number) => void;
  onRecordPayment: (doctorId: string) => void;
}

const DoctorPaymentCard: React.FC<DoctorPaymentCardProps> = ({ entry, onSetRate, onRecordPayment }) => {
  const [showCases, setShowCases]           = useState(false);
  const [showHistory, setShowHistory]       = useState(false);
  const [history, setHistory]               = useState<DoctorPaymentRecord[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError]     = useState<string | null>(null);

  // Invalidate cached history when a new payment lands (last_paid_at changes)
  useEffect(() => {
    setHistory(null);
  }, [entry.last_paid_at]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await getDoctorPayments(entry.doctor_id);
      setHistory(data);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }, [entry.doctor_id]);

  const handleToggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) fetchHistory();
  };

  const { current_balance, unpaid_case_count, total_paid_to_date, status_breakdown, unpaid_cases, last_paid_at, rate } = entry;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Doctor header */}
      <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
          <UserCircleIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white truncate">Dr. {entry.name}</p>
          <p className="text-xs text-gray-400 truncate">
            {[entry.specialization, entry.regNo ? `Reg. ${entry.regNo}` : ''].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex-shrink-0 text-right">
          {current_balance > 0 ? (
            <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmtINR(current_balance)} due</p>
          ) : last_paid_at ? (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
              <CheckCircleIcon className="w-4 h-4" /> Paid up
            </span>
          ) : (
            <span className="text-xs text-gray-400">No payments yet</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-4">
        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(status_breakdown).length > 0
            ? Object.entries(status_breakdown).map(([status, count]) => (
                <span key={status} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-700'}`}>
                  {count} {status}
                </span>
              ))
            : <span className="text-xs text-gray-400">No cases assigned</span>
          }
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <div>
            <p className="text-xs text-gray-400">Unpaid billable</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{unpaid_case_count} cases</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Rate</p>
            <div className="flex items-center gap-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{fmtINR(rate)}/case</p>
              <button onClick={() => onSetRate(entry.doctor_id, rate)}
                className="p-0.5 text-gray-400 hover:text-blue-500 transition-colors" title="Edit rate">
                <PencilSquareIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-400">Balance</p>
            <p className={`text-sm font-bold ${current_balance > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
              {fmtINR(current_balance)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Total paid</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{fmtINR(total_paid_to_date)}</p>
          </div>
          {last_paid_at && (
            <div>
              <p className="text-xs text-gray-400">Last payment</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{fmtDate(last_paid_at)}</p>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          {unpaid_case_count > 0 && (
            <button onClick={() => onRecordPayment(entry.doctor_id)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
              <BanknotesIcon className="w-4 h-4" /> Record Payment
            </button>
          )}
          {unpaid_case_count > 0 && (
            <button onClick={() => setShowCases(s => !s)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              {showCases ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />}
              {showCases ? 'Hide' : 'View'} cases
            </button>
          )}
          <button onClick={handleToggleHistory}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            {showHistory ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />}
            History
          </button>
        </div>

        {/* Unpaid cases table */}
        {showCases && unpaid_cases.length > 0 && (
          <div className="rounded-lg border border-gray-100 dark:border-gray-800 overflow-hidden text-xs">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400">Patient</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400">Type</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400">Since</th>
                </tr>
              </thead>
              <tbody>
                {unpaid_cases.map((c, i) => (
                  <tr key={c.doc_id} className={i % 2 !== 0 ? 'bg-gray-50/50 dark:bg-gray-800/20' : ''}>
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-200 truncate max-w-[160px]">
                      {c.full_name || c.doc_id}
                    </td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{c.certificate_type || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded-full font-semibold ${STATUS_STYLES[c.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-400">{c.billable_since ? fmtDate(c.billable_since) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Payment history — lazy loaded on expand */}
        {showHistory && (
          <div className="rounded-lg border border-gray-100 dark:border-gray-800 overflow-hidden text-xs">
            {historyLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-gray-400">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                Loading history…
              </div>
            ) : historyError ? (
              <div className="px-3 py-4 text-red-500">{historyError}</div>
            ) : !history || history.length === 0 ? (
              <div className="px-3 py-4 text-gray-400">No payment history.</div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400">Date</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400">Cases</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400">Amount</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((p, i) => (
                    <tr key={p.id} className={i % 2 !== 0 ? 'bg-gray-50/50 dark:bg-gray-800/20' : ''}>
                      <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{fmtDate(p.paid_at)}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{p.case_count}</td>
                      <td className="px-3 py-2 font-semibold text-gray-800 dark:text-gray-200">{fmtINR(p.amount)}</td>
                      <td className="px-3 py-2 text-gray-400">{p.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── DoctorPaymentsPage ───────────────────────────────────────────────────────

export const DoctorPaymentsPage: React.FC = () => {
  const [summary, setSummary]         = useState<BillingSummary | null>(null);
  const [isLoading, setIsLoading]     = useState(true);
  const [pageError, setPageError]     = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Default rate inline input — local copy synced from summary on load
  const [defaultRateInput, setDefaultRateInput] = useState('');

  // Edit per-doctor rate modal
  const [editRateFor, setEditRateFor]     = useState<string | null>(null);
  const [editRateValue, setEditRateValue] = useState('');

  // Record payment modal
  const [recordPaymentFor, setRecordPaymentFor] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    setPageError(null);
    try {
      const data = await getBillingSummary();
      setSummary(data);
      setDefaultRateInput(String(data.default_rate));
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load billing summary');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // ── Default rate ─────────────────────────────────────────────────────────

  const handleDefaultRateBlur = async () => {
    const rate = Number(defaultRateInput);
    if (!rate || rate === summary?.default_rate) return;
    setActionError(null);
    try {
      await setBillingRate(rate);
      await loadSummary();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update default rate');
    }
  };

  // ── Per-doctor rate modal ────────────────────────────────────────────────

  const handleOpenSetRate = (doctorId: string, currentRate: number) => {
    setEditRateFor(doctorId);
    setEditRateValue(String(currentRate));
  };

  const handleSaveRate = async () => {
    if (!editRateFor) return;
    const rate = Number(editRateValue);
    setActionError(null);
    try {
      await setDoctorRate(editRateFor, rate > 0 ? rate : null);
      setEditRateFor(null);
      await loadSummary();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save rate');
    }
  };

  // ── Record payment ───────────────────────────────────────────────────────

  const handleConfirmPayment = async (amount: number, note: string) => {
    if (!recordPaymentFor) return;
    setActionError(null);
    try {
      await recordDoctorPayment(recordPaymentFor, { amount, note });
      setRecordPaymentFor(null);
      await loadSummary();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to record payment');
    }
  };

  const recordPaymentEntry = summary?.doctors.find(d => d.doctor_id === recordPaymentFor) ?? null;

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3 text-center">
        <ExclamationCircleIcon className="w-10 h-10 text-red-400" />
        <p className="text-sm text-gray-600 dark:text-gray-400">{pageError}</p>
        <button onClick={loadSummary} className="text-sm text-blue-600 hover:underline">Retry</button>
      </div>
    );
  }

  const totals = summary!.totals;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

      {/* ── Action error banner ─────────────────────────────────────────────── */}
      {actionError && (
        <div className="flex items-center justify-between rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {actionError}
          <button onClick={() => setActionError(null)} className="ml-3 text-red-400 hover:text-red-600">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Doctor Payments</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Track billable cases and record payments per doctor
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold whitespace-nowrap">
              Default rate
            </span>
            <span className="text-sm text-gray-400">₹</span>
            <input
              type="number"
              value={defaultRateInput}
              onChange={e => setDefaultRateInput(e.target.value)}
              onBlur={handleDefaultRateBlur}
              onKeyDown={e => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
              className="w-16 text-sm font-semibold text-gray-900 dark:text-white bg-transparent focus:outline-none"
            />
            <span className="text-xs text-gray-400">/case</span>
          </div>
          <button onClick={loadSummary}
            className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            title="Refresh">
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ── Summary cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          {
            label: 'Total Doctors',
            value: totals.doctor_count,
            icon: <UserCircleIcon className="w-5 h-5" />,
            color: 'text-blue-500',
          },
          {
            label: 'Unpaid Billable Cases',
            value: totals.total_unpaid_cases,
            icon: <ClipboardDocumentCheckIcon className="w-5 h-5" />,
            color: 'text-emerald-500',
          },
          {
            label: 'Total Outstanding',
            value: fmtINR(totals.total_outstanding),
            icon: <BanknotesIcon className="w-5 h-5" />,
            color: 'text-amber-500',
          },
        ].map(card => (
          <div key={card.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className={`flex items-center gap-1.5 mb-1 ${card.color}`}>
              {card.icon}
              <span className="text-xs text-gray-400">{card.label}</span>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{card.value}</p>
          </div>
        ))}
      </div>

      {/* ── Doctor list ────────────────────────────────────────────────────── */}
      <div className="space-y-4">
        {summary!.doctors.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 px-6 py-12 text-center text-sm text-gray-400">
            No doctors found.
          </div>
        ) : (
          summary!.doctors.map(entry => (
            <DoctorPaymentCard
              key={entry.doctor_id}
              entry={entry}
              onSetRate={handleOpenSetRate}
              onRecordPayment={setRecordPaymentFor}
            />
          ))
        )}
      </div>

      {/* ── Edit Rate Modal ─────────────────────────────────────────────────── */}
      {editRateFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-xs p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                Rate for Dr. {summary!.doctors.find(d => d.doctor_id === editRateFor)?.name}
              </h2>
              <button onClick={() => setEditRateFor(null)}
                className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Rate per Billable Case (₹)
              </label>
              <input
                type="number"
                value={editRateValue}
                onChange={e => setEditRateValue(e.target.value)}
                autoFocus
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <p className="mt-1 text-xs text-gray-400">Set to 0 to revert to default (₹{summary!.default_rate}).</p>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setEditRateFor(null)}
                className="flex-1 py-2.5 text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                Cancel
              </button>
              <button onClick={handleSaveRate}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Record Payment Modal ────────────────────────────────────────────── */}
      {recordPaymentFor && recordPaymentEntry && (
        <RecordPaymentModal
          entry={recordPaymentEntry}
          onConfirm={handleConfirmPayment}
          onClose={() => setRecordPaymentFor(null)}
        />
      )}
    </div>
  );
};
